import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Input } from '../ui/input';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { streamChat, getChatHistory } from './api';

const SUGGESTIONS = [
  'Why was this month different from last month?',
  'What could I cut to save $10,000 a month?',
  'How much have we spent on marketing this year?',
  'What would revenue need to be for $100k profit a month?',
];

export const AskCfo = ({ open, onOpenChange, token }) => {
  const [sessionId] = useState(() => localStorage.getItem('cfo_session') || (() => { const s = `cfo-${Date.now()}`; localStorage.setItem('cfo_session', s); return s; })());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    if (open && token) getChatHistory(token, sessionId).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [open, token, sessionId]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    setBusy(true);
    try {
      await streamChat(token, sessionId, q, (chunk) => {
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: 'assistant', content: c[c.length - 1].content + chunk }; return c; });
      });
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: 'assistant', content: 'Sorry — I could not answer that just now.' }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-white border-l border-slate-200 w-full sm:max-w-md p-0 flex flex-col" data-testid="ask-cfo-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-200">
          <SheetTitle className="text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-lime-600" /> Ask your CFO</SheetTitle>
          <p className="text-xs text-slate-500">Answers come straight from your real numbers</p>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50/50">
          {messages.length === 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Try asking</p>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="cfo-suggestion"
                  className="w-full text-left text-sm text-slate-700 bg-white border border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/50 rounded-xl px-3.5 py-2.5 transition-colors">{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={cn('max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed',
                m.role === 'user' ? 'bg-slate-900 text-white font-medium' : 'bg-white border border-slate-200 text-slate-800')} data-testid={`cfo-msg-${m.role}`}>
                {m.content || (busy && i === messages.length - 1 ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : '')}
              </div>
            </div>
          ))}
        </div>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-2 bg-slate-100 rounded-full px-2 py-1">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="Ask about your money…" data-testid="cfo-chat-input"
              className="border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus-visible:ring-0" />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="cfo-chat-send"
              className="shrink-0 w-9 h-9 grid place-items-center rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AskCfo;
